<script setup lang="ts">
import { z } from 'zod';
import { computed } from 'vue';
import { UiForm, UiFormField, UiInputField, UiTextarea, UiSelect, UiText, UrgencyFormOptions } from '@shopexperts/shared';
import type { PublicExpertMinimal, PackagedServiceMinimal, Urgency } from '@shopexperts/shared';
import LeadStepHeader from './LeadStepHeader.vue';
import LeadStepActions from './LeadStepActions.vue';
import PreferredExpertCard from './PreferredExpertCard.vue';
import {
  formatProjectValueRangeId,
  parseProjectValueRangeId,
  PROJECT_VALUE_RANGE_SELECT_OPTIONS,
} from '@/app/constants/projectValueRanges';

interface Props {
  peerName?: string;
  currentStep: number;
  totalSteps: number;
  expert?: PublicExpertMinimal;
  packagedService?: PackagedServiceMinimal;
  isLoadingExpert?: boolean;
  source?: "expert" | "client";
}

const props = withDefaults(defineProps<Props>(), {
  expert: undefined,
  packagedService: undefined,
  isLoadingExpert: false,
  source: "client",
});

const headingText = computed(() => {
  if (props.peerName) return `Tell ${props.peerName} about your project`;
  if (props.expert?.name) return `Tell ${props.expert.name} about your project`;
  return 'Tell us about your project';
});

const subheadingText = computed(() => {
  if (props.peerName) return `Share the details of the project you’re starting together.`;
  if (props.expert?.name) {
    return `You’ve chosen ${props.expert.name} as your preferred expert. Share your project details so they can review your request.`;
  }
  return 'Provide the key details about your project so we can match you with the right expert.';
});

const selectedExpertName = computed(() => props.peerName || props.expert?.name);

const descriptionPlaceholder = computed(() => selectedExpertName.value
  ? `Describe your project goals, requirements, and any specific details that would help ${selectedExpertName.value} understand your needs...`
  : 'Describe your project goals, requirements, and any specific details that would help experts understand your needs...');

const attachmentsNote = computed(() => {
  if (props.source === 'expert') return '*Note:* You can share files, documents, and additional materials with the client after submitting the project.';
  if (selectedExpertName.value) return `*Note:* You can share files, documents, and additional materials with ${selectedExpertName.value} after submitting your request.`;
  return '*Note:* You can share files, documents, and additional materials with your matched expert after submitting your request.';
});

const emit = defineEmits<{
  (e: 'next', data: ProjectDetailsData): void;
}>();

export interface ProjectDetailsData {
  projectTitle: string;
  projectDescription?: string;
  urgency?: Urgency;
  projectValueRangeId?: number;
}

interface ProjectDetailsFormModel {
  projectTitle: string;
  projectDescription: string;
  urgency: string;
  projectValueRangeId: string;
}

const schema = z.object({
  projectTitle: z.preprocess(
    (val) => val === undefined || val === null ? '' : val,
    z.string()
      .min(1, 'Project title is required')
      .max(255, 'Project title must be 255 characters or less')
  ),
  projectDescription: z.preprocess(
    (val) => val === undefined || val === null ? '' : val,
    z.string().min(1, 'Project description is required')
  ),
  urgency: z.preprocess(
    (val) => val === undefined || val === null ? '' : val,
    z.string().min(1, 'Project timeline is required')
  ),
  projectValueRangeId: z.preprocess(
    (val) => val === undefined || val === null ? '' : val,
    z.string()
  ),
});

const model = defineModel<ProjectDetailsData>({
  default: () => ({
    projectTitle: '',
    projectDescription: '',
    urgency: undefined,
    projectValueRangeId: undefined,
  }),
});

const formModel = computed<ProjectDetailsFormModel>({
  get: () => ({
    projectTitle: model.value.projectTitle ?? '',
    projectDescription: model.value.projectDescription ?? '',
    urgency: model.value.urgency ?? '',
    projectValueRangeId: formatProjectValueRangeId(model.value.projectValueRangeId),
  }),
  set: (value) => {
    model.value = {
      projectTitle: value.projectTitle,
      projectDescription: value.projectDescription,
      urgency: value.urgency as Urgency,
      projectValueRangeId: parseProjectValueRangeId(value.projectValueRangeId),
    };
  },
});

function onSubmit(values: { projectTitle: string; projectDescription: string; urgency: string; projectValueRangeId: string }) {
  emit('next', {
    projectTitle: values.projectTitle,
    projectDescription: values.projectDescription,
    urgency: values.urgency as Urgency,
    projectValueRangeId: parseProjectValueRangeId(values.projectValueRangeId),
  });
}
</script>

<template>
  <div class="flex flex-col gap-6">
    <LeadStepHeader
      caption="Project Details"
      :heading="headingText"
      :subheading="subheadingText"
      :current-step="currentStep"
      :total-steps="totalSteps"
    >
      <template #close-button>
        <slot name="close-button" />
      </template>
    </LeadStepHeader>

    <!-- Preferred expert card (step 1 only, directly under the header) -->
    <PreferredExpertCard
      v-if="props.expert"
      :expert="props.expert"
      :packaged-service="props.packagedService"
    />
    <div
      v-else-if="props.isLoadingExpert"
      class="animate-pulse flex items-center gap-3 p-3 bg-se-gray-50 rounded-lg border border-se-gray-200"
    >
      <div class="w-10 h-10 bg-se-gray-200 rounded-full" />
      <div class="flex flex-col gap-2">
        <div class="w-32 h-4 bg-se-gray-200 rounded" />
        <div class="w-20 h-3 bg-se-gray-200 rounded" />
      </div>
    </div>

    <UiForm
      v-model="formModel"
      :schema="schema"
      @submit="onSubmit"
    >
      <div class="flex flex-col gap-5">
        <!-- Project Title -->
        <UiFormField
          name="projectTitle"
          label="Project Title"
          type="text"
        >
          <template #default="{ modelValue, onUpdateModelValue, onValidate, id, invalid, error }">
            <UiInputField
              :id="id"
              name="projectTitle"
              :model-value="modelValue as string"
              @update:model-value="onUpdateModelValue"
              @validate="onValidate"
              :invalid="invalid"
              :error="error"
              placeholder="e.g., Redesign my Shopify store homepage"
            />
          </template>
        </UiFormField>

        <!-- Project Description -->
        <UiFormField
          name="projectDescription"
          label="Project Description"
          type="textarea"
        >
          <template #default="{ modelValue, onUpdateModelValue, onValidate, id, invalid, error }">
            <UiTextarea
              :id="id"
              name="projectDescription"
              :model-value="modelValue as string"
              @update:model-value="onUpdateModelValue"
              @validate="onValidate"
              :invalid="invalid"
              :error="error"
              :placeholder="descriptionPlaceholder"
              :rows="5"
            />
          </template>
        </UiFormField>

        <div class="rounded-lg bg-se-gray-100 border border-se-gray-200 p-4">
          <UiText as="p" variant="p-small" :text="attachmentsNote" /> 
        </div>

        <!-- Urgency Select -->
        <UiFormField
          name="urgency"
          label="Project Timeline"
          type="select"
        >
          <template #default="{ modelValue, onUpdateModelValue, id, invalid, error }">
            <UiSelect
              :id="id"
              name="urgency"
              :model-value="modelValue as string"
              @update:model-value="onUpdateModelValue"
              :options="UrgencyFormOptions"
              placeholder="When do you need this completed?"
              :invalid="invalid"
              :error="error"
            />
          </template>
        </UiFormField>

        <UiFormField
          name="projectValueRangeId"
          label="Budget (optional)"
          type="select"
        >
          <template #default="{ modelValue, onUpdateModelValue, id, invalid, error }">
            <UiSelect
              :id="id"
              name="projectValueRangeId"
              :model-value="modelValue as string"
              @update:model-value="onUpdateModelValue"
              :options="PROJECT_VALUE_RANGE_SELECT_OPTIONS"
              placeholder="Select a budget range"
              :invalid="invalid"
              :error="error"
            />
          </template>
        </UiFormField>

        <LeadStepActions
          :current-step="currentStep"
          :total-steps="totalSteps"
          next-label="Continue"
        />
      </div>
    </UiForm>
  </div>
</template>
